import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseHref } from "service/api";
import {
  GetSiteApiResponse,
  mergeSiteConfig,
  WalletSiteConfig,
} from "config/siteBranding";
import { ResolvedWalletTheme } from "theme/walletTheme.types";

export type SiteResponse = WalletSiteConfig & {
  walletTheme?: ResolvedWalletTheme | null;
};

export type AgreementDto = {
  id: number;
  text: string;
  keyWords: string | null;
  link: string | null;
  translations?: Record<
    string,
    { text: string; keyWords?: string | null }
  > | null;
};

export const siteApi = createApi({
  reducerPath: "siteApi",
  baseQuery: fetchBaseQuery({
    baseUrl: baseHref,
    credentials: "include",
  }),
  tagTypes: ["Site", "Agreements"],
  endpoints: (builder) => ({
    getSite: builder.query<SiteResponse, void>({
      query: () => `/get-site`,
      transformResponse: (
        response: GetSiteApiResponse & { walletTheme?: ResolvedWalletTheme }
      ) => {
        if (!response?.result) {
          return mergeSiteConfig({});
        }
        const { result: _result, walletTheme, ...site } = response;
        const config = mergeSiteConfig({
          ...site,
          frontWalletAuthPageColor:
            site.frontWalletAuthPageColor || walletTheme?.authPageColor,
        });
        return {
          ...config,
          walletTheme: walletTheme ?? null,
        };
      },
      providesTags: ["Site"],
    }),

    getWalletTheme: builder.query<
      ResolvedWalletTheme | null,
      number | undefined
    >({
      query: (kycProgramId) => ({
        url: `/get-wallet-theme`,
        params: kycProgramId ? { kyc_program_id: kycProgramId } : undefined,
      }),
      transformResponse: (response: {
        result?: boolean;
        walletTheme?: ResolvedWalletTheme;
      }) =>
        response?.result && response.walletTheme ? response.walletTheme : null,
    }),

    getAgreementsList: builder.query<
      { result: boolean; agreements: AgreementDto[] },
      void
    >({
      query: () => `/agreements`,
      providesTags: ["Agreements"],
    }),
  }),
});

export const {
  useGetSiteQuery,
  useGetAgreementsListQuery,
  useGetWalletThemeQuery,
} = siteApi;
